import { useState } from 'react';
import { replayNotify } from '../../api/client';
import type { NotifyLogEntry } from '../../api/types';
import NotifyPayloadView from './NotifyPayloadView';

interface Props {
  entries: NotifyLogEntry[];
  onAfterReplay?: (entry: NotifyLogEntry) => void;
}

export default function NotifyLogTable({ entries, onAfterReplay }: Props) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleReplay(id: string) {
    if (!confirm('Replay this doorbell? Mastra will receive the original payload again.')) return;
    setPendingId(id);
    setError(null);
    try {
      const result = await replayNotify(id);
      onAfterReplay?.(result.entry);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingId(null);
    }
  }

  if (!entries.length) {
    return (
      <div className="card text-sm text-gray-500">No doorbells logged yet.</div>
    );
  }

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-4">Time</th>
            <th className="py-2 pr-4">Pebble</th>
            <th className="py-2 pr-4">Reason</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">Attempts</th>
            <th className="py-2 pr-4">Error</th>
            <th className="py-2 pr-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-b border-gray-100 align-top">
              <td className="py-2 pr-4 whitespace-nowrap text-xs text-gray-700">
                {new Date(e.createdAt).toLocaleString()}
              </td>
              <td className="py-2 pr-4 font-mono text-xs text-gray-700">{e.pebbleId.slice(0, 12)}…</td>
              <td className="py-2 pr-4">
                <ReasonBadge reason={e.reason} />
              </td>
              <td className="py-2 pr-4">
                <StatusBadge status={e.httpStatus} />
              </td>
              <td className="py-2 pr-4">{e.attempts}</td>
              <td className="py-2 pr-4 text-xs text-red-600 max-w-xs truncate" title={e.lastError ?? undefined}>
                {e.lastError ?? '—'}
              </td>
              <td className="py-2 pr-4">
                <button
                  type="button"
                  className="px-2 py-1 text-xs rounded bg-pebbl-600 text-white hover:bg-pebbl-700 disabled:opacity-50"
                  onClick={() => handleReplay(e.id)}
                  disabled={pendingId !== null}
                >
                  {pendingId === e.id ? '…' : 'Replay'}
                </button>
                <div className="mt-1">
                  <NotifyPayloadView payloadJson={e.payloadJson} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && (
        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: number | null }) {
  if (status == null) {
    return <span className="text-xs text-gray-500">pending</span>;
  }
  const ok = status >= 200 && status < 300;
  const cls = ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}

/**
 * Maps the `reason` field on a notify_log row (which mirrors the
 * RefinementStatus the doorbell was rung for) to a human label and a
 * tone color. New reasons land in the table without breaking — they fall
 * back to a neutral gray badge with the raw reason as the label.
 */
const REASON_LABELS: Record<string, { label: string; tone: ReasonTone }> = {
  questions_ready: { label: 'questions ready', tone: 'amber' },
  questions_ready_a: { label: 'questions ready (A)', tone: 'amber' },
  questions_ready_b: { label: 'questions ready (B)', tone: 'amber' },
  questions_ready_both: { label: 'questions ready (both)', tone: 'amber' },
  waiting_a: { label: 'waiting (A)', tone: 'blue' },
  waiting_b: { label: 'waiting (B)', tone: 'blue' },
  rematching: { label: 'rematching', tone: 'blue' },
  rematched: { label: 'rematched', tone: 'green' },
  escalated_consent: { label: 'consent', tone: 'green' },
  failed: { label: 'failed', tone: 'red' },
  cancelled: { label: 'cancelled', tone: 'gray' },
  // 07-DO-ALARM: rung once with this reason when retries exhaust.
  embed_failed: { label: 'embed failed', tone: 'red' },
};

type ReasonTone = 'amber' | 'blue' | 'green' | 'red' | 'gray';

const TONE_CLASSES: Record<ReasonTone, string> = {
  amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-700',
};

function ReasonBadge({ reason }: { reason: string }) {
  const meta = REASON_LABELS[reason] ?? { label: reason, tone: 'gray' as ReasonTone };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TONE_CLASSES[meta.tone]}`}
    >
      {meta.label}
    </span>
  );
}
